import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Check, Copy, Megaphone, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/pagination";
import { RowActions } from "@/components/ui/row-actions";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { listDomainsPaged } from "@/api/domains";
import {
    createCampaign,
    deleteCampaign,
    listCampaignsPaged,
    updateCampaign,
} from "@/api/campaigns";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import type { CampaignDto } from "@/types/campaign";
import { AdvertiserSelect } from "@/components/advertiser-select";
import { AffiliateSelect } from "@/components/affiliate-select";
import {
    AdvertiserCell,
    AffiliateCell,
    useAdvertiserLookup,
    useAffiliateLookup,
} from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

const STATUS_VARIANT: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
    paused: "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
    archived: "bg-muted text-muted-foreground",
};

export function CampaignsListPage(): React.ReactNode {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const PAGE_SIZE = 50;
    const [pageIndex, setPageIndex] = React.useState(0);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 250);
        return () => clearTimeout(t);
    }, [searchInput]);
    React.useEffect(() => setPageIndex(0), [search]);

    const [statusFilter, setStatusFilter] = React.useState<"all" | "active" | "paused" | "archived">("all");

    // Advertiser + Affiliate filters — URL-persisted. Both kept on this page
    // because the campaign sits at the intersection of the two counterparties.
    const [searchParams, setSearchParams] = useSearchParams();
    const advertiserId = ownerFilterFromUrl(searchParams.get("advertiserId"));
    const affiliateId = ownerFilterFromUrl(searchParams.get("affiliateId"));
    const setAdvertiserId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("advertiserId");
                    else p.set("advertiserId", v);
                    return p;
                },
                { replace: true },
            );
            setPageIndex(0);
        },
        [setSearchParams],
    );
    const setAffiliateId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("affiliateId");
                    else p.set("affiliateId", v);
                    return p;
                },
                { replace: true },
            );
            setPageIndex(0);
        },
        [setSearchParams],
    );
    const advLookup = useAdvertiserLookup();
    const affLookup = useAffiliateLookup();
    const showAdvertiserColumn = advLookup.advertisers.length > 0;
    const showAffiliateColumn = affLookup.affiliates.length > 0;

    const campaignsQuery = useQuery({
        queryKey: ["campaigns", pageIndex, search, statusFilter, advertiserId, affiliateId],
        queryFn: () => listCampaignsPaged({
            page: pageIndex,
            size: PAGE_SIZE,
            sort: "createdAt,desc",
            q: search || undefined,
            status: statusFilter,
            advertiserId: ownerFilterParam(advertiserId),
            affiliateId: ownerFilterParam(affiliateId),
        }),
        placeholderData: (prev) => prev,
    });
    const [createOpen, setCreateOpen] = React.useState(false);
    // Lazy-load: only fetch the domains list when the operator actually opens
    // the create dialog. Was previously fetching size:200 on every list render.
    const domainsQuery = useQuery({
        queryKey: ["domains-for-campaign"],
        queryFn: () => listDomainsPaged({ size: 200, sort: "hostname,asc" }),
        enabled: createOpen,
    });

    const rows: CampaignDto[] = campaignsQuery.data?.content ?? [];

    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [editName, setEditName] = React.useState("");

    const createMut = useMutation({
        mutationFn: (req: { domainId: number; name: string; landingPath?: string; notes?: string }) =>
            createCampaign(req),
        onSuccess: (c) => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            toast.success(`Campaign "${c.name}" created`);
            setCreateOpen(false);
            navigate(`/campaigns/${c.id}`);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Couldn't create campaign")),
    });
    const renameMut = useMutation({
        mutationFn: ({ id, name }: { id: number; name: string }) => updateCampaign(id, { name }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            setEditingId(null);
            toast.success("Renamed");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Rename failed")),
    });
    const statusMut = useMutation({
        mutationFn: ({ id, status }: { id: number; status: "active" | "paused" | "archived" }) =>
            updateCampaign(id, { status }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["campaigns"] }),
        onError: (err) => toast.error(apiErrorMessage(err, "Status change failed")),
    });
    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteCampaign(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
            toast.success("Deleted");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const copyLink = (link: string | null) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(
            () => toast.success("Link copied"),
            () => toast.error("Couldn't copy"),
        );
    };

    return (
        <>
            <PageHeader
                title="Campaigns"
                description="Affiliate-link buckets per domain. Each campaign has a stable shareable URL; clicks and leads are attributed back to it."
                actions={
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-campaign-button">
                                <Plus className="h-4 w-4" />
                                New campaign
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <CreateCampaignForm
                                domains={domainsQuery.data?.content ?? []}
                                pending={createMut.isPending}
                                onSubmit={(req) => createMut.mutate(req)}
                                onCancel={() => setCreateOpen(false)}
                            />
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="space-y-3 px-6 py-4">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 min-w-[260px]">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search by name…"
                            className="h-7 border-0 bg-transparent px-0 focus-visible:ring-0"
                            data-testid="campaigns-search"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                        <SelectTrigger className="w-[160px]" data-testid="campaigns-status-filter">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                    {showAdvertiserColumn && (
                        <div className="w-[200px]">
                            <AdvertiserSelect
                                value={advertiserId}
                                onChange={setAdvertiserId}
                                data-testid="campaigns-advertiser-filter"
                            />
                        </div>
                    )}
                    {showAffiliateColumn && (
                        <div className="w-[200px]">
                            <AffiliateSelect
                                value={affiliateId}
                                onChange={setAffiliateId}
                                data-testid="campaigns-affiliate-filter"
                            />
                        </div>
                    )}
                </div>

                {campaignsQuery.isLoading ? (
                    <div className="flex justify-center py-12"><Spinner /></div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<Megaphone className="h-8 w-8" />}
                        title={search ? `No campaigns match "${search}"` : "No campaigns yet"}
                        description={search ? "Try a different search term." : "Create one and share its link with your affiliate."}
                        action={!search ? <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New campaign</Button> : undefined}
                    />
                ) : (
                    <div className="overflow-hidden rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="campaigns-table">
                            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-left">Domain</th>
                                    <th className="px-3 py-2 text-left">Affiliate link</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    {showAdvertiserColumn && (
                                        <th className="px-3 py-2 text-left">Advertiser</th>
                                    )}
                                    {showAffiliateColumn && (
                                        <th className="px-3 py-2 text-left">Affiliate</th>
                                    )}
                                    <th className="px-3 py-2 text-left">Created</th>
                                    <th className="w-32 px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((c) => (
                                    <tr key={c.id} className="group hover:bg-muted/30" data-testid={`campaign-row-${c.id}`}>
                                        <td className="px-3 py-2">
                                            {editingId === c.id ? (
                                                <form
                                                    className="flex items-center gap-1.5"
                                                    onSubmit={(e) => {
                                                        e.preventDefault();
                                                        const v = editName.trim();
                                                        if (v && v !== c.name) renameMut.mutate({ id: c.id, name: v });
                                                        else setEditingId(null);
                                                    }}
                                                >
                                                    <Input autoFocus className="h-7" value={editName}
                                                        onChange={(e) => setEditName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === "Escape") setEditingId(null); }}
                                                    />
                                                    <Button type="submit" size="sm" variant="ghost" disabled={renameMut.isPending}>
                                                        <Check className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                                        <X className="h-3.5 w-3.5" />
                                                    </Button>
                                                </form>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <Link to={`/campaigns/${c.id}`} className="font-medium hover:underline">
                                                        {c.name}
                                                    </Link>
                                                    <span className="font-mono text-[10px] text-muted-foreground">{c.slug}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Link to={`/domains/${c.domainId}`} className="text-muted-foreground hover:underline">
                                                {c.domainHostname || `domain #${c.domainId}`}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2">
                                            {c.link ? (
                                                <button
                                                    type="button"
                                                    onClick={() => copyLink(c.link)}
                                                    className="group/link inline-flex max-w-[420px] items-center gap-1.5 rounded border border-border bg-background px-2 py-1 font-mono text-[11px] hover:bg-muted"
                                                    title={`Copy: ${c.link}`}
                                                    data-testid={`copy-link-${c.id}`}
                                                >
                                                    <Copy className="h-3 w-3 text-muted-foreground group-hover/link:text-foreground" />
                                                    <span className="truncate">{c.link}</span>
                                                </button>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <Select
                                                value={c.status}
                                                onValueChange={(v) => statusMut.mutate({ id: c.id, status: v as "active" | "paused" | "archived" })}
                                            >
                                                <SelectTrigger className={cn("h-7 w-[110px] border-0 px-2", STATUS_VARIANT[c.status])}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="active">Active</SelectItem>
                                                    <SelectItem value="paused">Paused</SelectItem>
                                                    <SelectItem value="archived">Archived</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        {showAdvertiserColumn && (
                                            <td className="px-3 py-2">
                                                <AdvertiserCell advertiserId={c.advertiserId} lookup={advLookup.byId} />
                                            </td>
                                        )}
                                        {showAffiliateColumn && (
                                            <td className="px-3 py-2">
                                                <AffiliateCell affiliateId={c.affiliateId} lookup={affLookup.byId} />
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(c.createdAt)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <RowActions>
                                                <Button
                                                    type="button" size="sm" variant="ghost"
                                                    title="Open detail"
                                                    aria-label={`Open ${c.name}`}
                                                    onClick={() => navigate(`/campaigns/${c.id}`)}
                                                    data-testid={`open-campaign-${c.id}`}
                                                >
                                                    <ArrowRight className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    type="button" size="sm" variant="ghost"
                                                    title="Rename"
                                                    aria-label={`Rename ${c.name}`}
                                                    onClick={() => { setEditingId(c.id); setEditName(c.name); }}
                                                    data-testid={`rename-campaign-${c.id}`}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    type="button" size="sm" variant="ghost"
                                                    title="Delete"
                                                    aria-label={`Delete ${c.name}`}
                                                    onClick={() => confirm.show({
                                                        title: `Delete "${c.name}"?`,
                                                        description: "Past click + lead history stays attributed to this campaign id, but you'll no longer be able to point new traffic at it.",
                                                        destructive: true,
                                                        confirmLabel: "Delete campaign",
                                                        onConfirm: async () => {
                                                            await new Promise<void>((resolve, reject) => {
                                                                deleteMut.mutate(c.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                                            });
                                                        },
                                                    })}
                                                    data-testid={`delete-campaign-${c.id}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                                </Button>
                                            </RowActions>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {campaignsQuery.data && rows.length > 0 && (
                    <Pagination page={campaignsQuery.data} pageSize={PAGE_SIZE} currentPage={pageIndex} onPage={setPageIndex} />
                )}
            </div>
            {confirm.dialog}
        </>
    );
}

function CreateCampaignForm({
    domains,
    pending,
    onSubmit,
    onCancel,
}: {
    domains: Array<{ id: number; hostname: string; status: string }>;
    pending: boolean;
    onSubmit: (req: { domainId: number; name: string; landingPath?: string; notes?: string }) => void;
    onCancel: () => void;
}): React.ReactNode {
    // Only domains the worker is actually serving from. Provisioning-in-flight
    // (PURCHASING / NS_DELEGATED / ZONE_ACTIVE) shows up in the unfiltered list
    // but a campaign on one would 404 visitors until it finishes.
    const ASSIGNABLE = new Set(["AVAILABLE", "ASSIGNED", "WORKER_ATTACHED"]);
    const usable = domains.filter((d) => ASSIGNABLE.has(d.status));
    const [domainId, setDomainId] = React.useState<string>("");
    const [name, setName] = React.useState("");
    const [landingPath, setLandingPath] = React.useState("/");
    const [notes, setNotes] = React.useState("");

    return (
        <>
            <DialogHeader>
                <DialogTitle>New campaign</DialogTitle>
                <DialogDescription>
                    Pick a domain and give the campaign a name. The shareable affiliate URL is generated automatically.
                </DialogDescription>
            </DialogHeader>
            <form
                className="space-y-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit({ domainId: Number(domainId), name: name.trim(), landingPath: landingPath.trim() || "/", notes: notes.trim() || undefined });
                }}
            >
                <div className="space-y-2">
                    <Label htmlFor="campaign-domain">Domain</Label>
                    <Select value={domainId} onValueChange={setDomainId}>
                        <SelectTrigger id="campaign-domain" data-testid="campaign-domain-select">
                            <SelectValue placeholder="Choose a domain…" />
                        </SelectTrigger>
                        <SelectContent>
                            {usable.map((d) => (
                                <SelectItem key={d.id} value={String(d.id)}>{d.hostname}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="campaign-name">Name</Label>
                    <Input id="campaign-name" data-testid="campaign-name-input"
                        placeholder="e.g. Mediabuyers Q2 — Facebook"
                        value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="campaign-path">Landing path</Label>
                    <Input id="campaign-path" placeholder="/" value={landingPath} onChange={(e) => setLandingPath(e.target.value)} />
                    <p className="text-xs text-muted-foreground">Where the affiliate link drops visitors. Defaults to <code>/</code>.</p>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="campaign-notes">Notes (optional)</Label>
                    <Textarea id="campaign-notes" placeholder="Internal notes — partner contact, payout terms, etc."
                        value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                </div>
                <DialogFooter>
                    <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>Cancel</Button>
                    <Button type="submit" data-testid="create-campaign-submit"
                        disabled={!domainId || !name.trim() || pending}>
                        {pending ? <Spinner size="sm" /> : "Create campaign"}
                    </Button>
                </DialogFooter>
            </form>
        </>
    );
}
